import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { authApi } from '../lib/api';
import { UserPlus, AlertCircle, Zap, Building2 } from 'lucide-react';

export function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Hasla nie sa zgodne');
      return;
    }

    if (password.length < 8) {
      setError('Haslo musi miec minimum 8 znakow');
      return;
    }

    setLoading(true);

    try {
      const { data } = await authApi.registerTenant({ email, password, companyName });
      setAuth(data.token, data.user, data.tenant);
      navigate('/');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Wystapil blad rejestracji');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-dark min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Animated background orbs */}
      <div className="orb-cyan w-96 h-96 -top-48 -left-48" />
      <div className="orb-purple w-80 h-80 top-1/2 -right-40" style={{ animationDelay: '2s' }} />
      <div className="orb-pink w-64 h-64 -bottom-32 left-1/4" style={{ animationDelay: '4s' }} />

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `linear-gradient(rgba(0,245,255,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(0,245,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}
      />

      <div className="max-w-md w-full px-4 relative z-10">
        {/* Logo */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-accent-primary to-accent-secondary mb-4 shadow-accent-primary/20">
            <Zap className="w-8 h-8 text-dark-900" />
          </div>
          <h1 className="text-4xl font-bold text-gradient">Ecomflow</h1>
          <p className="mt-2 text-text-secondary">Zarejestruj nowe konto firmowe</p>
        </div>

        {/* Register card */}
        <div className="card animate-slide-up">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger animate-scale-in">
                <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div>
              <label htmlFor="companyName" className="label">
                Nazwa firmy
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                <input
                  id="companyName"
                  type="text"
                  required
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="input pl-10"
                  placeholder="Moja Firma Sp. z o.o."
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="label">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="twoj@email.pl"
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                Haslo
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="Minimum 8 znakow"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="label">
                Potwierdz haslo
              </label>
              <input
                id="confirmPassword"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                placeholder="Powtorz haslo"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <div className="loader w-5 h-5 mr-2" />
                  Rejestracja...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <UserPlus className="w-5 h-5 mr-2" />
                  Zarejestruj konto
                </span>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-border-subtle text-center">
            <Link
              to="/login"
              className="text-sm text-text-secondary hover:text-accent-primary transition-colors"
            >
              Masz juz konto? Zaloguj sie
            </Link>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-xs text-text-disabled">
          Ecomflow &copy; 2026 - Platforma e-commerce
        </p>
      </div>
    </div>
  );
}
